import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { strategies } from '@/mocks/strategies';
import SeoHead from '@/components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.diariode1trader.com';

export default function StrategyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const strategy = strategies.find((s) => s.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const strategySchema = strategy
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: strategy.name,
          description: strategy.description.slice(0, 160),
          image: strategy.image,
          author: {
            '@type': 'Organization',
            name: 'Diário de 1 Trader',
            url: siteUrl,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Diário de 1 Trader',
            url: siteUrl,
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${siteUrl}/estrategias/${strategy.id}`,
          },
          url: `${siteUrl}/estrategias/${strategy.id}`,
          inLanguage: 'pt-PT',
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Início',
              item: `${siteUrl}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Estratégias',
              item: `${siteUrl}/estrategias`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: strategy.name,
              item: `${siteUrl}/estrategias/${strategy.id}`,
            },
          ],
        },
      ]
    : undefined;

  if (!strategy) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center pt-28 pb-20">
          <i className="ri-flashlight-line text-6xl text-slate-200 mb-4"></i>
          <h1 className="text-2xl font-bold text-[#0d1f3c] mb-2">Estratégia não encontrada</h1>
          <p className="text-slate-400 mb-6">A estratégia que procuras não existe ou foi removida.</p>
          <Link to="/estrategias" className="bg-blue-600 text-white font-semibold px-6 py-3 rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap">
            Ver todas as estratégias
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const related = strategies.filter((s) => s.id !== strategy.id).slice(0, 3);

  const riskColor = strategy.risk === 'Alto'
    ? 'bg-red-100 text-red-700'
    : strategy.risk === 'Médio' || strategy.risk === 'Médio-Alto'
    ? 'bg-yellow-100 text-yellow-700'
    : 'bg-green-100 text-green-700';

  const difficultyColor = strategy.difficulty === 'Avançado'
    ? 'bg-red-100 text-red-700'
    : strategy.difficulty === 'Intermédio' || strategy.difficulty === 'Iniciante-Intermédio'
    ? 'bg-yellow-100 text-yellow-700'
    : 'bg-green-100 text-green-700';

  const isEmaCross = id === 'scalping';
  const isEma200 = id === 'day-trading';
  const is1HCandle = id === 'swing-trading';
  const isImpulse = id === 'breakout';
  const isHHLL = id === 'trend-following';
  const isVwap = id === 'orderflow';

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={`${strategy.name} | Estratégia de Futuros | Diário de 1 Trader`}
        description={`${strategy.description.slice(0, 155)}...`}
        keywords={`${strategy.name}, estratégia trading futuros, ${strategy.indicators.join(', ')}`}
        canonical={`/estrategias/${strategy.id}`}
        schema={strategySchema}
      />

      {/* Hero da estratégia */}
      <div className="relative w-full h-64 md:h-[480px]">
        <img src={strategy.image} alt={strategy.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/90 via-[#0d1f3c]/50 to-transparent"></div>
        <div className="absolute inset-0 flex flex-col justify-end max-w-5xl mx-auto px-4 md:px-8 pb-10 md:pb-14 left-0 right-0">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/estrategias" className="flex items-center gap-1.5 text-blue-300 hover:text-white text-sm transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              Estratégias
            </Link>
            <span className="text-slate-400 text-sm">/</span>
            <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{strategy.category}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">{strategy.name}</h1>
          <p className="text-blue-200 text-sm md:text-base mt-2">{strategy.tagline}</p>
        </div>
      </div>

      {/* Stats rápidos */}
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-b border-slate-100">
          <div className="text-center">
            <div className="text-xs text-slate-400 mb-1">Timeframe</div>
            <div className="font-bold text-[#0d1f3c] text-sm">{strategy.timeframe}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-slate-400 mb-1">Win Rate</div>
            <div className="font-bold text-blue-600 text-sm">{strategy.winRate}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-slate-400 mb-1">Risco/Retorno</div>
            <div className="font-bold text-blue-600 text-sm">{strategy.riskReward}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-slate-400 mb-1">Dificuldade</div>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${difficultyColor}`}>{strategy.difficulty}</span>
          </div>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">

          {/* Coluna principal */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-4">O que é o {strategy.name}?</h2>
            <p className="text-slate-600 leading-relaxed text-base mb-8">{strategy.description}</p>

            {/* Secção especial EMA Cross */}
            {isEmaCross && (
              <>
                {/* Como funciona */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-5">Como Funciona o Cruzamento</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 border border-green-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full">
                          <i className="ri-arrow-up-line text-green-600 text-lg"></i>
                        </span>
                        <h3 className="font-bold text-green-700 text-sm">Golden Cross — COMPRA</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        A <strong>EMA 9</strong> cruza <strong>acima</strong> da EMA 20. Indica que o momentum de curto prazo está a superar o de médio prazo — sinal de tendência bullish. Entrar long após o cruzamento confirmado no fecho da vela.
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full">
                          <i className="ri-arrow-down-line text-red-500 text-lg"></i>
                        </span>
                        <h3 className="font-bold text-red-600 text-sm">Death Cross — VENDA</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        A <strong>EMA 9</strong> cruza <strong>abaixo</strong> da EMA 20. Indica que o momentum bearish está a dominar. Entrar short após confirmação do cruzamento no fecho da vela.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Regras de entrada */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-5">Regras de Entrada e Saída</h2>
                  <div className="space-y-3">
                    {[
                      { step: '1', title: 'Confirmar tendência no H1 ou H4', desc: 'Antes de entrar, verifica se o timeframe superior está alinhado. Não compres num mercado em tendência de baixa no H1.' },
                      { step: '2', title: 'Aguardar cruzamento no M15', desc: 'O setup principal é feito no M15. Espera que a EMA 9 cruze a EMA 20 e a vela feche acima (compra) ou abaixo (venda).' },
                      { step: '3', title: 'Confirmar com volume ou RSI', desc: 'Para maior probabilidade, confirma com volume acima da média ou RSI acima de 50 (compra) / abaixo de 50 (venda).' },
                      { step: '4', title: 'Entrada na abertura da vela seguinte', desc: 'Não entres na vela do cruzamento — entra na abertura da vela seguinte para evitar falsos sinais.' },
                      { step: '5', title: 'Stop Loss abaixo/acima da EMA 20', desc: 'Coloca o stop loss imediatamente abaixo da EMA 20 (compra) ou acima da EMA 20 (venda). Ajusta com ATR para volatilidade.' },
                      { step: '6', title: 'Take Profit com risco mínimo 1:2', desc: 'Define o TP num rácio mínimo de 1:2. Em tendências fortes, usa trailing stop seguindo a EMA 20 para maximizar o ganho.' },
                    ].map((item) => (
                      <div key={item.step} className="flex gap-4 items-start">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#0d1f3c] text-white font-bold text-xs rounded-full shrink-0 mt-0.5">
                          {item.step}
                        </div>
                        <div>
                          <div className="font-semibold text-[#0d1f3c] text-sm">{item.title}</div>
                          <div className="text-slate-500 text-sm leading-relaxed">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dica de ouro */}
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-lightbulb-flash-line text-amber-500 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-amber-800 text-sm mb-1">Dica Pro — Evita Mercados em Range</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        O maior inimigo do EMA Cross é um mercado lateral. Antes de operar, verifica se o ADX está acima de 20 — se sim, há tendência suficiente para o setup funcionar. Se o ADX estiver abaixo de 20, evita entrar em cruzamentos pois terás muitos falsos sinais e pequenas perdas consecutivas.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Secção especial EMA 200 Bounce */}
            {isEma200 && (
              <>
                {/* Os 3 pilares do método */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Os 3 Pilares do Método EMA 200</h2>
                  <p className="text-slate-500 text-sm mb-5">Este método assenta em três condições simples que devem estar todas presentes antes de entrar numa operação.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      {
                        num: '1',
                        title: 'Tendência Definida',
                        icon: 'ri-line-chart-line',
                        color: 'bg-blue-50 border-blue-100',
                        titleColor: 'text-[#0d1f3c]',
                        desc: 'O preço está claramente acima da EMA 200 (tendência de alta) ou abaixo (tendência de baixa). Nunca operes contra a EMA 200.',
                      },
                      {
                        num: '2',
                        title: 'Recuo à EMA 200',
                        icon: 'ri-arrow-go-back-line',
                        color: 'bg-amber-50 border-amber-100',
                        titleColor: 'text-amber-800',
                        desc: 'O preço afasta-se da EMA 200 e depois regressa para a tocar ou aproximar-se. Este recuo é a oportunidade — é onde o "bounce" acontece.',
                      },
                      {
                        num: '3',
                        title: 'Vela de Confirmação',
                        icon: 'ri-checkbox-circle-line',
                        color: 'bg-green-50 border-green-100',
                        titleColor: 'text-green-700',
                        desc: 'Aguarda uma vela de confirmação (bullish engulfing, pin bar ou hammer) que mostre rejeição da EMA 200. Só então entras na operação.',
                      },
                    ].map((p) => (
                      <div key={p.num} className={`border rounded-2xl p-5 ${p.color}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-[#0d1f3c] text-white font-bold text-xs rounded-full shrink-0">
                            {p.num}
                          </div>
                          <h3 className={`font-bold text-sm ${p.titleColor}`}>{p.title}</h3>
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed">{p.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Regras passo a passo */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-5">Regras de Entrada e Saída</h2>
                  <div className="space-y-3">
                    {[
                      {
                        step: '1',
                        title: 'Adiciona a EMA 200 ao gráfico',
                        desc: 'Usa qualquer plataforma (TradingView, NinjaTrader, etc.) e adiciona a Exponential Moving Average com período 200. Funciona em qualquer timeframe — começa pelo H1 ou H4.',
                      },
                      {
                        step: '2',
                        title: 'Determina o lado da tendência',
                        desc: 'Preço acima da EMA 200 = só procuras entradas LONG. Preço abaixo da EMA 200 = só procuras entradas SHORT. Esta regra elimina 50% dos erros de um trader.',
                      },
                      {
                        step: '3',
                        title: 'Aguarda o recuo (pullback) à EMA 200',
                        desc: 'Não entras quando o preço está muito afastado da EMA 200. Espera pacientemente que o preço regresse e toque ou se aproxime da linha. Este é o momento do setup.',
                      },
                      {
                        step: '4',
                        title: 'Confirma com vela de reversão',
                        desc: 'Na zona da EMA 200, aguarda uma vela que mostre rejeição clara: pin bar, hammer, bullish/bearish engulfing. Esta é a tua confirmação de entrada — sem ela, não entras.',
                      },
                      {
                        step: '5',
                        title: 'Entra na abertura da vela seguinte',
                        desc: 'Após a vela de confirmação fechar, entra no início da vela seguinte. Stop loss imediatamente abaixo da EMA 200 (long) ou acima (short), com margem de alguns pips/ticks.',
                      },
                      {
                        step: '6',
                        title: 'Gestão do trade — mínimo 1:2',
                        desc: 'Define take profit no mínimo ao dobro do stop loss (rácio 1:2). Em tendências fortes, usa trailing stop acompanhando a EMA 200 para deixar o lucro correr ao máximo.',
                      },
                    ].map((item) => (
                      <div key={item.step} className="flex gap-4 items-start">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#0d1f3c] text-white font-bold text-xs rounded-full shrink-0 mt-0.5">
                          {item.step}
                        </div>
                        <div>
                          <div className="font-semibold text-[#0d1f3c] text-sm">{item.title}</div>
                          <div className="text-slate-500 text-sm leading-relaxed">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Porque funciona */}
                <div className="bg-[#0d1f3c] rounded-2xl p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-question-answer-line text-blue-400 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-white text-sm mb-2">Porque é que a EMA 200 funciona tão bem?</h4>
                      <p className="text-blue-200 text-sm leading-relaxed">
                        A EMA 200 é seguida por milhões de traders, bancos, fundos de investimento e algoritmos em todo o mundo. Quando tantos participantes olham para o mesmo nível, ele torna-se uma <strong className="text-white">profecia auto-realizável</strong> — todos reagem ao mesmo tempo, criando o bounce que tu estás à espera. Quanto maior o timeframe, mais poderoso é o nível.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dica pro */}
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-lightbulb-flash-line text-amber-500 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-amber-800 text-sm mb-1">Dica Pro — Multi-Timeframe</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Para aumentares a probabilidade de sucesso, verifica a EMA 200 em múltiplos timeframes. Se o preço está a fazer bounce na EMA 200 no H1 <strong>e</strong> está acima da EMA 200 no H4 e no Diário — tens confluência de 3 timeframes. Estes são os setups com maior taxa de sucesso do método.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Secção especial 1H Candle */}
            {is1HCandle && (
              <>
                {/* Como funciona — dual timeframe */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">A Lógica do Dual Timeframe</h2>
                  <p className="text-slate-500 text-sm mb-5">Esta estratégia combina dois timeframes com funções distintas — o H1 define o contexto, o M1 executa a entrada.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#0d1f3c] rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full shrink-0">
                          <i className="ri-time-line text-white text-base"></i>
                        </span>
                        <h3 className="font-bold text-white text-sm">Gráfico 1H — O Mapa</h3>
                      </div>
                      <p className="text-blue-200 text-sm leading-relaxed">
                        O H1 é onde identificas o padrão. Procuras uma vela de 1H que forme um movimento claro (alta ou baixa) e depois o preço regress ao interior dessa vela. Esse regresso é o sinal de alerta — hora de mudar para o M1.
                      </p>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-slate-200 rounded-full shrink-0">
                          <i className="ri-flashlight-line text-slate-600 text-base"></i>
                        </span>
                        <h3 className="font-bold text-[#0d1f3c] text-sm">Gráfico 1M — A Entrada</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        No M1 esperas que o preço forme pequenas estruturas de alta/baixa e quebre o high ou low da vela de M1 anterior a favor da tendência do H1. Esse breakout no M1 é a tua entrada — com stop de menos de meia vela de 1H.
                      </p>
                    </div>
                  </div>
                </div>

                {/* O padrão passo a passo */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-5">O Padrão — Passo a Passo</h2>
                  <div className="space-y-3">
                    {[
                      {
                        step: '1',
                        title: 'Abre o gráfico H1 e identifica a tendência',
                        desc: 'Observa a direção do mercado no H1. Está a fazer higher highs e higher lows? Tendência de alta. Lower highs e lower lows? Tendência de baixa. Só operas a favor desta direção.',
                      },
                      {
                        step: '2',
                        title: 'Identifica a vela de 1H de referência',
                        desc: 'Procura uma vela de 1H que tenha feito um movimento forte na direção da tendência. Marca o high e low dessa vela — ela vai ser a tua zona de referência.',
                      },
                      {
                        step: '3',
                        title: 'Aguarda o pullback ao interior da vela',
                        desc: 'Espera que o preço recue e entre novamente para o interior da vela de 1H marcada. Este recuo é o "reset" do mercado — está a dar-te uma segunda oportunidade de entrar.',
                      },
                      {
                        step: '4',
                        title: 'Muda para o M1 e procura o setup de entrada',
                        desc: 'No M1, o preço está a formar mini-estruturas. Espera que forme um pequeno uptrend/downtrend e quebre o high (entrada long) ou low (entrada short) da última vela de M1.',
                      },
                      {
                        step: '5',
                        title: 'Entra no breakout do high/low do M1',
                        desc: 'Quando o M1 quebra o high/low anterior na direção certa, entras. Stop loss abaixo do low mais recente do M1 — normalmente menos de metade de uma vela de 1H de distância.',
                      },
                      {
                        step: '6',
                        title: 'Take profit no high/low da vela de 1H',
                        desc: 'O teu alvo natural é o extreme (high ou low) da vela de 1H de referência. Em tendências fortes, podes deixar correr para o próximo nível de estrutura do H1.',
                      },
                    ].map((item) => (
                      <div key={item.step} className="flex gap-4 items-start">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#0d1f3c] text-white font-bold text-xs rounded-full shrink-0 mt-0.5">
                          {item.step}
                        </div>
                        <div>
                          <div className="font-semibold text-[#0d1f3c] text-sm">{item.title}</div>
                          <div className="text-slate-500 text-sm leading-relaxed">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Exemplos visuais */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Exemplos do Setup</h2>
                  <p className="text-slate-500 text-sm mb-5">Como o padrão aparece nos dois timeframes — H1 para contexto, M1 para entrada precisa.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="w-full h-52">
                        <img
                          src="https://public.readdy.ai/ai/img_res/493e40c60993767931b6fbdf0df56bc4.jpg"
                          alt="1H Candle — padrão no H1"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="bg-slate-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-time-line text-[#0d1f3c] text-base"></i>
                          </span>
                          <span className="text-[#0d1f3c] font-semibold text-xs">Gráfico H1 — Identificar o Padrão</span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1 leading-relaxed">Vela de 1H de referência marcada. O preço recua ao seu interior — zona de alerta para baixar ao M1.</p>
                      </div>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="w-full h-52">
                        <img
                          src="https://readdy.ai/api/search-image?query=1%20minute%20chart%20scalp%20entry%20breakout%20previous%20high%20low%20price%20action%20small%20candle%20structure%20trading%20professional%20clean%20dark%20financial%20precise%20entry&width=600&height=400&seq=1hcandle2&orientation=landscape"
                          alt="1H Candle — entrada no M1"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="bg-blue-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-crosshair-line text-blue-600 text-base"></i>
                          </span>
                          <span className="text-blue-700 font-semibold text-xs">Gráfico M1 — Entrada Precisa</span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1 leading-relaxed">No M1 o preço quebra o high da vela anterior — entrada ativada. Stop abaixo do low mais recente, risco mínimo.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Porquê funciona */}
                <div className="bg-[#0d1f3c] rounded-2xl p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-question-answer-line text-blue-400 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-white text-sm mb-2">Porque é que este padrão funciona?</h4>
                      <p className="text-blue-200 text-sm leading-relaxed">
                        Quando o preço forma uma vela de 1H forte e depois recua para o seu interior, os traders que perderam a primeira entrada ficam à espera de uma segunda oportunidade. O M1 mostra exatamente o momento em que o mercado retoma a direção — é o ponto onde a maioria dos <strong className="text-white">smart money reentram</strong>. O stop pequeno existe porque se o padrão for válido, o preço não deve voltar muito para trás.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dica pro */}
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-lightbulb-flash-line text-amber-500 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-amber-800 text-sm mb-1">Dica Pro — Sessão de Londres e Nova Iorque</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Este setup funciona melhor durante a <strong>abertura de Londres (8h GMT)</strong> e a <strong>abertura de Nova Iorque (13h30 GMT)</strong> — quando há volume suficiente para que os padrões do H1 se resolvam rapidamente. Evita aplicar esta estratégia durante horas de baixa liquidez (Ásia ou fim de tarde europeu) onde os padrões ficam inconsistentes e os stops são facilmente atingidos.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Secção especial Impulse Entry */}
            {isImpulse && (
              <>
                {/* Impulso vs Pullback */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Como o Mercado Realmente se Move</h2>
                  <p className="text-slate-500 text-sm mb-5">O mercado nunca sobe nem desce em linha reta. Move-se sempre em impulsos seguidos de retrações — e é exatamente aí que está a oportunidade.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#0d1f3c] rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full shrink-0">
                          <i className="ri-arrow-right-up-line text-white text-base"></i>
                        </span>
                        <h3 className="font-bold text-white text-sm">O Impulso — O Movimento</h3>
                      </div>
                      <p className="text-blue-200 text-sm leading-relaxed">
                        Um impulso é um movimento forte, rápido e direccional no H4, H1 ou M30. Tem velas grandes, poucos pavios e volume elevado. É o movimento que <strong className="text-white">define a direção</strong> — onde o smart money entrou com força.
                      </p>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-slate-200 rounded-full shrink-0">
                          <i className="ri-arrow-go-back-line text-slate-600 text-base"></i>
                        </span>
                        <h3 className="font-bold text-[#0d1f3c] text-sm">O Pullback — A Oportunidade</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Após o impulso, o preço recua naturalmente — é o mercado a "respirar". Este pullback é a tua janela de entrada. <strong>Não entras no impulso</strong> — entras quando o preço retoma o impulso após a retração, com risco mínimo.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Os 3 elementos do setup */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Os 3 Elementos do Setup</h2>
                  <p className="text-slate-500 text-sm mb-5">Todos os três têm de estar presentes. Se um faltar, o setup não é válido.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      {
                        num: '1',
                        title: 'Impulso Forte',
                        color: 'bg-blue-50 border-blue-100',
                        titleColor: 'text-[#0d1f3c]',
                        desc: 'Um movimento impulsivo claro no H4, H1 ou M30. Velas grandes, corpo cheio, na direção da tendência. Sem este impulso inicial, não há setup.',
                      },
                      {
                        num: '2',
                        title: 'Pullback ao Range',
                        color: 'bg-amber-50 border-amber-100',
                        titleColor: 'text-amber-800',
                        desc: 'O preço recua para dentro do range do impulso — idealmente 30%a 50% do movimento. O pullback não deve ser tão profundo que quebre a estrutura do impulso.',
                      },
                      {
                        num: '3',
                        title: 'Retoma do Impulso',
                        color: 'bg-green-50 border-green-100',
                        titleColor: 'text-green-700',
                        desc: 'Uma vela que mostra que o preço está a retomar a direção original — engulfing, momentum candle ou break do high/low da última vela do pullback.',
                      },
                    ].map((p) => (
                      <div key={p.num} className={`border rounded-2xl p-5 ${p.color}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-[#0d1f3c] text-white font-bold text-xs rounded-full shrink-0">
                            {p.num}
                          </div>
                          <h3 className={`font-bold text-sm ${p.titleColor}`}>{p.title}</h3>
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed">{p.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Passo a passo */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-5">Regras de Entrada e Gestão</h2>
                  <div className="space-y-3">
                    {[
                      {
                        step: '1',
                        title: 'Identifica o impulso no H4 ou H1',
                        desc: 'Procura um movimento impulsivo forte — mínimo 3 a 5 velas grandes na mesma direção, com corpo cheio e sem grandes pavios. Este é o teu impulso de referência.',
                      },
                      {
                        step: '2',
                        title: 'Marca o range completo do impulso',
                        desc: 'Marca o high e low do impulso. Este range é a tua referência para o target — o objetivo é capturar 50% desse range no movimento seguinte após o pullback.',
                      },
                      {
                        step: '3',
                        title: 'Aguarda o pullback — não entras ainda',
                        desc: 'O preço vai recuar após o impulso. Deixa recuar — não perdes nada por esperar. O pullback ideal fica entre 30% e 50% do range do impulso. Pullback acima de 60%? Setup inválido.',
                      },
                      {
                        step: '4',
                        title: 'Confirma a retoma — entra no breakout',
                        desc: 'Quando o preço para de recuar e quebra o high da última vela do pullback (em long) ou o low (em short), é a tua entrada. Esta é a confirmação de que o impulso está a retomar.',
                      },
                      {
                        step: '5',
                        title: 'Stop loss abaixo do low do pullback',
                        desc: 'O stop loss vai imediatamente abaixo do low mais recente do pullback (long) ou acima do high (short). Se o preço voltar a esse ponto, o setup foi invalidado — sai sem hesitar.',
                      },
                      {
                        step: '6',
                        title: 'Target: 50% do range do impulso original',
                        desc: 'O alvo primário é 50% do range do impulso que identificaste no passo 2. É um target conservador e altamente atingível — em condições fortes, deixa correr para o topo/fundo do impulso.',
                      },
                    ].map((item) => (
                      <div key={item.step} className="flex gap-4 items-start">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#0d1f3c] text-white font-bold text-xs rounded-full shrink-0 mt-0.5">
                          {item.step}
                        </div>
                        <div>
                          <div className="font-semibold text-[#0d1f3c] text-sm">{item.title}</div>
                          <div className="text-slate-500 text-sm leading-relaxed">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Exemplos visuais */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Exemplos do Setup</h2>
                  <p className="text-slate-500 text-sm mb-5">O impulso, o pullback e a entrada — como identificar cada fase no gráfico.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="w-full h-52">
                        <img
                          src="https://readdy.ai/api/search-image?query=bullish%20impulse%20move%20strong%20momentum%20candles%20upward%20price%20action%20followed%20by%20pullback%20retracement%20trading%20chart%20H1%20H4%20professional%20dark%20clean%20financial&width=600&height=400&seq=impulse1&orientation=landscape"
                          alt="Impulse Entry — impulso bullish e pullback"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="bg-green-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-up-circle-fill text-green-600"></i>
                          </span>
                          <span className="text-green-700 font-semibold text-xs">Impulso Bullish — Entrada Long</span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1 leading-relaxed">Movimento impulsivo forte para cima, pullback de 40-50%, entrada no breakout do high do pullback. Stop abaixo do low da retração.</p>
                      </div>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="w-full h-52">
                        <img
                          src="https://readdy.ai/api/search-image?query=bearish%20impulse%20move%20strong%20momentum%20candles%20downward%20price%20action%20pullback%20retest%20entry%20trading%20chart%20H1%20professional%20dark%20clean%20financial%20short%20entry&width=600&height=400&seq=impulse2&orientation=landscape"
                          alt="Impulse Entry — impulso bearish e pullback"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="bg-red-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-down-circle-fill text-red-500"></i>
                          </span>
                          <span className="text-red-600 font-semibold text-xs">Impulso Bearish — Entrada Short</span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1 leading-relaxed">Queda impulsiva forte, pullback de alta para 30-50% do range, entrada no break do low do pullback. Stop acima do high da retração.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* NY Open */}
                <div className="bg-[#0d1f3c] rounded-2xl p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-time-zone-line text-blue-400 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-white text-sm mb-2">NY Open — O Melhor Momento para este Setup</h4>
                      <p className="text-blue-200 text-sm leading-relaxed">
                        A Impulse Entry funciona em qualquer sessão, mas é <strong className="text-white">especialmente poderosa na abertura de Nova Iorque (13h30–15h GMT)</strong>. Nesta janela, os grandes players entram no mercado com volume elevado, criando impulsos fortes e pullbacks limpos — é quando os setups são mais rápidos, mais limpos e com maior follow-through.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dica pro */}
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-lightbulb-flash-line text-amber-500 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-amber-800 text-sm mb-1">Dica Pro — Valida o Impulso com Volume</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Nem todos os movimentos são impulsos válidos. Para garantir que o impulso é real, verifica se o <strong>volume no impulso é visivelmente superior ao volume no pullback</strong>. Volume alto no impulso + volume baixo no pullback = mercado a "respirar" antes de retomar. Volume alto no pullback = possível reversão — fica fora.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Secção especial HH/LL */}
            {isHHLL && (
              <>
                {/* As duas tendências */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">A Linguagem do Mercado</h2>
                  <p className="text-slate-500 text-sm mb-5">O mercado só faz duas coisas — tendência ou lateralização. Aprender a identificar a estrutura é a base de tudo.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 border border-green-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full shrink-0">
                          <i className="ri-arrow-up-line text-green-600 text-lg"></i>
                        </span>
                        <h3 className="font-bold text-green-700 text-sm">Tendência de Alta — HH + HL</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed mb-3">
                        O mercado faz <strong>Higher Highs</strong> (cada máximo é maior que o anterior) e <strong>Higher Lows</strong> (cada mínimo é maior que o anterior). Enquanto esta estrutura se mantiver, a tendência está intacta — só operas long.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">HH — Higher High</span>
                        <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">HL — Higher Low</span>
                      </div>
                    </div>
                    <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full shrink-0">
                          <i className="ri-arrow-down-line text-red-500 text-lg"></i>
                        </span>
                        <h3 className="font-bold text-red-600 text-sm">Tendência de Baixa — LH + LL</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed mb-3">
                        O mercado faz <strong>Lower Highs</strong> (cada máximo é menor que o anterior) e <strong>Lower Lows</strong> (cada mínimo é menor que o anterior). Enquanto esta estrutura se mantiver, só operas short.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        <span className="bg-red-100 text-red-600 text-xs font-bold px-2.5 py-1 rounded-full">LH — Lower High</span>
                        <span className="bg-red-100 text-red-600 text-xs font-bold px-2.5 py-1 rounded-full">LL — Lower Low</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BOS e CHoCH */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Os Dois Sinais Chave</h2>
                  <p className="text-slate-500 text-sm mb-5">Além de identificar a tendência, há dois eventos críticos que precisas de reconhecer para entrar e sair no momento certo.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#0d1f3c] rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full shrink-0">
                          <i className="ri-flashlight-line text-white text-base"></i>
                        </span>
                        <h3 className="font-bold text-white text-sm">BOS — Break of Structure</h3>
                      </div>
                      <p className="text-blue-200 text-sm leading-relaxed">
                        Quando o preço quebra um High anterior (em alta) ou um Low anterior (em baixa), <strong className="text-white">confirma a continuação da tendência</strong>. É o sinal de entrada — o mercado acabou de confirmar que a estrutura se mantém.
                      </p>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full shrink-0">
                          <i className="ri-repeat-line text-amber-600 text-base"></i>
                        </span>
                        <h3 className="font-bold text-amber-800 text-sm">CHoCH — Change of Character</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Quando o preço quebra o <strong>último Higher Low</strong> (em tendência de alta) ou o <strong>último Lower High</strong> (em baixa), a estrutura foi violada. <strong>Sai do trade</strong> ou prepara-te para a reversão.
                      </p>
                    </div>
                  </div>
                </div>

                {/* EMAs de apoio à tendência */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">EMAs que Confirmam a Tendência</h2>
                  <p className="text-slate-500 text-sm mb-5">As médias móveis exponenciais funcionam como camadas de filtro — cada uma confirma a força da tendência identificada pela estrutura HH/LL.</p>
                  <div className="space-y-3">
                    {[
                      {
                        ema: 'EMA 20',
                        role: 'Tendência de Curto Prazo',
                        color: 'border-l-green-400',
                        badge: 'bg-green-100 text-green-700',
                        desc: 'A EMA 20 segue o preço de perto e é a primeira linha de suporte/resistência dinâmica. Em tendência de alta, o preço tende a fazer pullbacks até à EMA 20 e ressaltar — cada bounce na EMA 20 é um Higher Low em formação. Se o preço fechar consistentemente abaixo dela, a tendência de curto prazo está enfraquecida.',
                      },
                      {
                        ema: 'EMA 50',
                        role: 'Tendência de Médio Prazo',
                        color: 'border-l-amber-400',
                        badge: 'bg-amber-100 text-amber-700',
                        desc: 'A EMA 50 é o filtro de tendência médio mais usado por traders profissionais. Quando o preço está acima da EMA 50, a tendência de médio prazo é bullish — só procuras longs. Abaixo da EMA 50, só procuras shorts. Os melhores setups HH/LL surgem quando a estrutura e a EMA 50 estão alinhadas na mesma direção.',
                      },
                      {
                        ema: 'EMA 200',
                        role: 'Tendência de Longo Prazo',
                        color: 'border-l-red-400',
                        badge: 'bg-red-100 text-red-700',
                        desc: 'A EMA 200 define a macro tendência. Quando o preço está acima dela, instituições e fundos estão em modo de compra. Abaixo dela, estão em modo de venda. A confluência perfeita é: preço acima da EMA 200, estrutura HH/HL intacta no H1, e pullback ao Higher Low — é aqui que os setups de maior probabilidade acontecem.',
                      },
                    ].map((item) => (
                      <div key={item.ema} className={`border-l-4 ${item.color} bg-white border border-slate-100 rounded-r-2xl pl-4 pr-5 py-4`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${item.badge}`}>{item.ema}</span>
                          <span className="text-slate-400 text-xs">{item.role}</span>
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>

                  {/* Tabela de confluência */}
                  <div className="mt-5 bg-[#f0f6ff] rounded-2xl p-5">
                    <h4 className="font-bold text-[#0d1f3c] text-sm mb-3">Confluência de EMAs + Estrutura — Níveis de Probabilidade</h4>
                    <div className="space-y-2">
                      {[
                        { label: 'Estrutura HH/HL + acima EMA 20 + EMA 50 + EMA 200', level: 'Muito Alta', color: 'bg-green-500' },
                        { label: 'Estrutura HH/HL + acima EMA 50 + EMA 200', level: 'Alta', color: 'bg-green-400' },
                        { label: 'Estrutura HH/HL + acima EMA 200 apenas', level: 'Média', color: 'bg-amber-400' },
                        { label: 'Estrutura HH/HL sem confirmação de EMAs', level: 'Baixa', color: 'bg-red-400' },
                      ].map((row) => (
                        <div key={row.label} className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${row.color}`}></div>
                          <span className="text-slate-600 text-xs flex-1">{row.label}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                            row.level === 'Muito Alta' ? 'bg-green-100 text-green-700' :
                            row.level === 'Alta' ? 'bg-green-50 text-green-600' :
                            row.level === 'Média' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-600'
                          }`}>{row.level}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Passo a passo */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-5">Como Operar com Estrutura de Mercado</h2>
                  <div className="space-y-3">
                    {[
                      {
                        step: '1',
                        title: 'Identifica a estrutura no H4 ou H1',
                        desc: 'Abre o gráfico e marca os últimos swing highs e swing lows. O mercado está a fazer HH+HL? É tendência de alta. LH+LL? Tendência de baixa. Só operas a favor desta direção.',
                      },
                      {
                        step: '2',
                        title: 'Aguarda o pullback ao último Higher Low (ou Lower High)',
                        desc: 'Depois de um BOS, o preço tipicamente recua para testar o último swing point. Este pullback ao HL (em alta) ou LH (em baixa) é a tua zona de entrada — risco mínimo, alvo máximo.',
                      },
                      {
                        step: '3',
                        title: 'Confirma com vela de reversão na zona',
                        desc: 'Na zona do HL/LH, aguarda uma vela de confirmação: pin bar, engulfing ou inside bar. Esta vela prova que o mercado rejeitou o nível e está pronto para retomar a tendência.',
                      },
                      {
                        step: '4',
                        title: 'Entra após confirmação — stop no swing anterior',
                        desc: 'Entra na direção da tendência após a vela de confirmação. Stop loss fica abaixo do Higher Low (long) ou acima do Lower High (short) — a estrutura invalida-se se o swing anterior for violado.',
                      },
                      {
                        step: '5',
                        title: 'Alvo no próximo nível de estrutura',
                        desc: 'O take profit natural é o próximo Higher High (em alta) ou Lower Low (em baixa). Em tendências fortes, usa trailing stop seguindo os novos swing points para capturar movimentos maiores.',
                      },
                      {
                        step: '6',
                        title: 'Sai imediatamente em CHoCH',
                        desc: 'Se o preço quebrar o teu Higher Low de referência (em long) ou Lower High (em short), a estrutura mudou. Fecha a posição — não esperes pelo stop. O mercado disse-te que errou.',
                      },
                    ].map((item) => (
                      <div key={item.step} className="flex gap-4 items-start">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#0d1f3c] text-white font-bold text-xs rounded-full shrink-0 mt-0.5">
                          {item.step}
                        </div>
                        <div>
                          <div className="font-semibold text-[#0d1f3c] text-sm">{item.title}</div>
                          <div className="text-slate-500 text-sm leading-relaxed">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Exemplos visuais */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Exemplos Visuais da Estrutura</h2>
                  <p className="text-slate-500 text-sm mb-5">Como identificar HH/HL e LH/LL num gráfico real — e onde entrar em cada caso.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="w-full h-52">
                        <img
                          src="https://readdy.ai/api/search-image?query=higher%20high%20higher%20low%20uptrend%20market%20structure%20chart%20swing%20points%20bullish%20price%20action%20annotated%20trading%20professional%20dark%20financial%20clean&width=600&height=400&seq=hhll1&orientation=landscape"
                          alt="Higher High Higher Low — tendência de alta"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="bg-green-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-up-circle-fill text-green-600"></i>
                          </span>
                          <span className="text-green-700 font-semibold text-xs">HH + HL — Tendência de Alta</span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1 leading-relaxed">Cada máximo supera o anterior (HH) e cada mínimo fica acima do mínimo anterior (HL). Entrada no recuo ao HL após BOS.</p>
                      </div>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="w-full h-52">
                        <img
                          src="https://readdy.ai/api/search-image?query=lower%20high%20lower%20low%20downtrend%20market%20structure%20chart%20swing%20points%20bearish%20price%20action%20annotated%20trading%20professional%20dark%20financial%20clean&width=600&height=400&seq=hhll2&orientation=landscape"
                          alt="Lower High Lower Low — tendência de baixa"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="bg-red-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-down-circle-fill text-red-500"></i>
                          </span>
                          <span className="text-red-600 font-semibold text-xs">LH + LL — Tendência de Baixa</span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1 leading-relaxed">Cada máximo fica abaixo do anterior (LH) e cada mínimo cai abaixo do mínimo anterior (LL). Entrada no reteste ao LH após BOS.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Porquê funciona */}
                <div className="bg-[#0d1f3c] rounded-2xl p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-question-answer-line text-blue-400 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-white text-sm mb-2">Porque é que a estrutura HH/LL é tão poderosa?</h4>
                      <p className="text-blue-200 text-sm leading-relaxed">
                        Esta não é uma estratégia inventada — é a <strong className="text-white">definição matemática de tendência</strong> segundo Dow Theory, criada em 1900. Todos os traders, bancos, fundos e algoritmos usam esta lógica para definir tendência. Quando operas com a estrutura, não estás a adivinhar — estás a seguir a lógica do próprio mercado. É o fundamento sobre o qual <strong className="text-white">todas as outras estratégias se apoiam</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dica pro */}
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-lightbulb-flash-line text-amber-500 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-amber-800 text-sm mb-1">Dica Pro — Combina com Zonas de Oferta e Procura</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Os setups de maior probabilidade surgem quando o recuo ao Higher Low acontece <strong>numa zona de procura (demand zone)</strong> previamente identificada. Isso significa confluência de estrutura + zona institucional — dois motivos para o preço reagir no mesmo sítio. Estes são os trades com menor risco e maior potencial de toda a estratégia HH/LL.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Secção especial VWAP */}
            {isVwap && (
              <>
                {/* O que é o VWAP */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">O que é o VWAP e Porque Importa</h2>
                  <p className="text-slate-500 text-sm mb-5">O VWAP não é um indicador qualquer — é o nível de referência que bancos, fundos e algoritmos usam para executar ordens ao longo do dia.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-[#0d1f3c] rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full shrink-0">
                          <i className="ri-bar-chart-2-line text-white text-base"></i>
                        </span>
                        <h3 className="font-bold text-white text-sm">O Que é o VWAP</h3>
                      </div>
                      <p className="text-blue-200 text-sm leading-relaxed">
                        VWAP = preço médio ponderado pelo volume desde o início da sessão. Significa que cada transação pesa mais ou menos consoante o volume. É recalculado tick a tick e reseta a zero no início de cada dia.
                      </p>
                    </div>
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-slate-200 rounded-full shrink-0">
                          <i className="ri-bank-line text-slate-600 text-base"></i>
                        </span>
                        <h3 className="font-bold text-[#0d1f3c] text-sm">Porque os Institucionais Usam</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Fundos e bancos usam o VWAP como benchmark para execução de ordens grandes. Comprar abaixo do VWAP = execução acima da média. Isto cria pressão real no nível — <strong>não é especulativo</strong>.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Os 3 setups principais */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Os 3 Setups do VWAP</h2>
                  <p className="text-slate-500 text-sm mb-5">Há três formas distintas de operar com o VWAP — cada uma com as suas condições e lógica específica.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      {
                        num: '1',
                        title: 'Bounce no VWAP',
                        color: 'bg-blue-50 border-blue-100',
                        titleColor: 'text-[#0d1f3c]',
                        desc: 'O preço afasta-se do VWAP, regressa para o tocar e ressalta na direção da tendência do dia. É o setup mais frequente e com maior probabilidade — operado com o trend.',
                      },
                      {
                        num: '2',
                        title: 'Breakout do VWAP',
                        color: 'bg-green-50 border-green-100',
                        titleColor: 'text-green-700',
                        desc: 'O preço estava abaixo do VWAP (tendência bearish) e quebra com força acima — ou vice-versa. Esta quebra com volume alto sinaliza uma mudança de controlo no mercado.',
                      },
                      {
                        num: '3',
                        title: 'Rejeição nas Bandas SD',
                        color: 'bg-amber-50 border-amber-100',
                        titleColor: 'text-amber-800',
                        desc: 'O VWAP tem desvios padrão (SD+1, SD+2 acima; SD-1, SD-2 abaixo). Quando o preço alcança SD+1 ou SD+2 e é rejeitado, é sinal de reversão para o VWAP central.',
                      },
                    ].map((p) => (
                      <div key={p.num} className={`border rounded-2xl p-5 ${p.color}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-[#0d1f3c] text-white font-bold text-xs rounded-full shrink-0">
                            {p.num}
                          </div>
                          <h3 className={`font-bold text-sm ${p.titleColor}`}>{p.title}</h3>
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed">{p.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* VWAP acima vs abaixo */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Acima ou Abaixo do VWAP — A Regra de Ouro</h2>
                  <p className="text-slate-500 text-sm mb-5">Antes de qualquer entrada, a primeira coisa que chegas é: o preço está acima ou abaixo do VWAP?</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 border border-green-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full shrink-0">
                          <i className="ri-arrow-up-line text-green-600 text-lg"></i>
                        </span>
                        <h3 className="font-bold text-green-700 text-sm">Preço Acima do VWAP — Bullish</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed mb-3">
                        Os compradores estão no controlo. O VWAP funciona como <strong>suporte dinâmico</strong>. Procuras apenas entradas long — nos pullbacks ao VWAP ou no breakout acima do VWAP com volume. Shorts são de alto risco.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">Só entradas LONG</span>
                        <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">VWAP = Suporte</span>
                      </div>
                    </div>
                    <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full shrink-0">
                          <i className="ri-arrow-down-line text-red-500 text-lg"></i>
                        </span>
                        <h3 className="font-bold text-red-600 text-sm">Preço Abaixo do VWAP — Bearish</h3>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed mb-3">
                        Os vendedores controlam o dia. O VWAP é <strong>resistência dinâmica</strong>. Procuras apenas entradas short — nos bounces de alta até ao VWAP ou no breakout abaixo. Longs são de alto risco.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        <span className="bg-red-100 text-red-600 text-xs font-bold px-2.5 py-1 rounded-full">Só entradas SHORT</span>
                        <span className="bg-red-100 text-red-600 text-xs font-bold px-2.5 py-1 rounded-full">VWAP = Resistência</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Passo a passo */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-5">Regras de Entrada e Gestão</h2>
                  <div className="space-y-3">
                    {[
                      {
                        step: '1',
                        title: 'Adiciona o VWAP ao gráfico (M5 ou M15)',
                        desc: 'O VWAP está disponível gratuitamente em TradingView, NinjaTrader, Sierra Chart e todas as plataformas de futuros. Adiciona o indicador VWAP standard e, se possível, as bandas de desvio padrão SD+1 e SD-1.',
                      },
                      {
                        step: '2',
                        title: 'Define o contexto do dia — acima ou abaixo',
                        desc: 'Nos primeiros 15-30 minutos da sessão, observa onde o preço se posiciona em relação ao VWAP. Se abre e mantém-se acima = dia bullish. Se abre e cai abaixo = dia bearish. Este contexto define o teu bias para o resto do dia.',
                      },
                      {
                        step: '3',
                        title: 'Aguarda o pullback ao VWAP',
                        desc: 'Não entras quando o preço está muito afastado do VWAP — o risco é desnecessariamente alto. Espera que o preço regresse ao VWAP. O pullback ideal é suave, com volume a diminuir — mostra fraqueza dos contra-traders.',
                      },
                      {
                        step: '4',
                        title: 'Confirma com vela de reversão no VWAP',
                        desc: 'No nível do VWAP, aguarda uma vela que mostre rejeição clara: pin bar, hammer, engulfing ou inside bar. No M5 é suficiente. Sem confirmação, não entras — o preço pode atravessar o VWAP.',
                      },
                      {
                        step: '5',
                        title: 'Entra na abertura da vela seguinte — stop apertado',
                        desc: 'Entra na vela seguinte à de confirmação. Stop loss fica do outro lado do VWAP — normalmente apenas 3-5 ticks de distância. É um dos stops mais pequenos de qualquer estratégia intraday.',
                      },
                      {
                        step: '6',
                        title: 'Target: próximo nível de estrutura ou SD+1/-1',
                        desc: 'O target primário é o máximo ou mínimo mais recente da sessão, ou a banda de desvio padrão SD+1 (long) ou SD-1 (short). Rácios de 1:2 e 1:3 são facilmente atingíveis. Em dias de tendência forte, trailing stop.',
                      },
                    ].map((item) => (
                      <div key={item.step} className="flex gap-4 items-start">
                        <div className="w-8 h-8 flex items-center justify-center bg-[#0d1f3c] text-white font-bold text-xs rounded-full shrink-0 mt-0.5">
                          {item.step}
                        </div>
                        <div>
                          <div className="font-semibold text-[#0d1f3c] text-sm">{item.title}</div>
                          <div className="text-slate-500 text-sm leading-relaxed">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Exemplos visuais */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Exemplos do Setup</h2>
                  <p className="text-slate-500 text-sm mb-5">Como o VWAP bounce e o VWAP breakout aparecem num gráfico intraday real.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="w-full h-52">
                        <img
                          src="https://readdy.ai/api/search-image?query=VWAP%20bounce%20long%20entry%20intraday%20trading%20chart%20M5%20price%20pullback%20to%20VWAP%20line%20candlestick%20bullish%20reversal%20confirmation%20volume%20professional%20dark%20clean%20financial&width=600&height=400&seq=vwap1&orientation=landscape"
                          alt="VWAP Bounce Long"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="bg-green-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-up-circle-fill text-green-600"></i>
                          </span>
                          <span className="text-green-700 font-semibold text-xs">VWAP Bounce — Entrada Long</span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1 leading-relaxed">Preço acima do VWAP, pullback suave ao nível, vela de confirmação bullish, entrada long com stop abaixo do VWAP.</p>
                      </div>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-slate-100">
                      <div className="w-full h-52">
                        <img
                          src="https://readdy.ai/api/search-image?query=VWAP%20resistance%20rejection%20short%20entry%20intraday%20trading%20chart%20M5%20price%20bounce%20off%20VWAP%20line%20bearish%20candlestick%20dark%20professional%20clean%20financial%20trading&width=600&height=400&seq=vwap2&orientation=landscape"
                          alt="VWAP Rejeição Short"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="bg-red-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-down-circle-fill text-red-500"></i>
                          </span>
                          <span className="text-red-600 font-semibold text-xs">VWAP Rejeição — Entrada Short</span>
                        </div>
                        <p className="text-slate-500 text-xs mt-1 leading-relaxed">Preço abaixo do VWAP, bounce de alta até ao nível, vela bearish de rejeição, entrada short com stop acima do VWAP.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bandas SD */}
                <div className="mb-8">
                  <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">As Bandas de Desvio Padrão — SD+1 e SD-1</h2>
                  <p className="text-slate-500 text-sm mb-5">As bandas do VWAP mostram onde o preço está estatisticamente sobrecomprado ou sobrevendido dentro da sessão.</p>
                  <div className="space-y-3">
                    {[
                      {
                        band: 'SD+1 (Banda Superior)',
                        role: 'Sobrecomprado Intraday',
                        color: 'border-l-green-400',
                        badge: 'bg-green-100 text-green-700',
                        desc: 'Quando o preço alcança SD+1 (1 desvio padrão acima do VWAP), está estatisticamente sobrecomprado para a sessão. Em dias de ranging, é zona de reversão short para o VWAP central. Em dias de tendência forte, pode ser zona de continuação — o contexto do dia define a leitura correta.',
                      },
                      {
                        band: 'SD-1 (Banda Inferior)',
                        role: 'Sobrevendido Intraday',
                        color: 'border-l-red-400',
                        badge: 'bg-red-100 text-red-700',
                        desc: 'Quando o preço cai até SD-1 (1 desvio padrão abaixo do VWAP), está sobrevendido para a sessão. Em dias de ranging, é zona de reversão long de regresso ao VWAP. Em dias de tendência forte de baixa, pode ser zona de continuação — o contexto do dia define a leitura correta.',
                      },
                      {
                        band: 'SD+2 / SD-2',
                        role: 'Extremos Estatísticos',
                        color: 'border-l-amber-400',
                        badge: 'bg-amber-100 text-amber-700',
                        desc: 'O segundo desvio padrão (SD+2 / SD-2) representa um extremo estatístico raro. Quando o preço chega a estes níveis, as reversões são muito mais prováveis — mesmo em dias de trending. São zonas de alto interesse para scalpers que operam reversões de curto prazo.',
                      },
                    ].map((item) => (
                      <div key={item.band} className={`border-l-4 ${item.color} bg-white border border-slate-100 rounded-r-2xl pl-4 pr-5 py-4`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${item.badge}`}>{item.band}</span>
                          <span className="text-slate-400 text-xs">{item.role}</span>
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Porque funciona */}
                <div className="bg-[#0d1f3c] rounded-2xl p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-question-answer-line text-blue-400 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-white text-sm mb-2">Porque é que o VWAP funciona de forma tão consistente?</h4>
                      <p className="text-blue-200 text-sm leading-relaxed">
                        Ao contrário de outros indicadores baseados apenas no preço, o VWAP incorpora <strong className="text-white">volume real de mercado</strong>. Os algoritmos de execução institucional são programados para comparar os seus preços de execução com o VWAP — comprar abaixo e vender acima. Isso significa que em cada dia de mercado, <strong className="text-white">há literalmente biliões de dólares em ordens algorítmicas que gravitam em torno do VWAP</strong>. Não é um nível que escolheste — é um nível que o próprio mercado criou.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tabela de contexto */}
                <div className="bg-[#f0f6ff] rounded-2xl p-5 mb-8">
                  <h4 className="font-bold text-[#0d1f3c] text-sm mb-3">Tipo de Dia — Como Ajustar a Estratégia</h4>
                  <div className="space-y-2">
                    {[
                      { label: 'Dia de Tendência Forte: preço afasta-se do VWAP e não regressa', tipo: 'Trending', color: 'bg-blue-500', acao: 'Entra no breakout, trailing stop. Evita reversões.' },
                      { label: 'Dia de Range: preço oscila entre SD+1 e SD-1 regularmente', tipo: 'Ranging', color: 'bg-amber-400', acao: 'Reversões nas bandas SD, target VWAP central.' },
                      { label: 'Dia com Pullback limpo ao VWAP e retoma', tipo: 'Bounce', color: 'bg-green-500', acao: 'Melhor setup — bounce no VWAP a favor da tendência.' },
                      { label: 'Dia de Low Volume / News Day: VWAP instável', tipo: 'Evitar', color: 'bg-red-400', acao: 'Fica fora. VWAP perde precisão sem volume consistente.' },
                    ].map((row) => (
                      <div key={row.label} className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${row.color}`}></div>
                        <div className="flex-1">
                          <span className="text-slate-600 text-xs">{row.label}</span>
                          <div className="text-slate-400 text-xs mt-0.5 italic">{row.acao}</div>
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                          row.tipo === 'Evitar' ? 'bg-red-100 text-red-600' :
                          row.tipo === 'Ranging' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }`}>{row.tipo}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dica pro */}
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-8">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-lightbulb-flash-line text-amber-500 text-xl"></i>
                    </span>
                    <div>
                      <h4 className="font-bold text-amber-800 text-sm mb-1">Dica Pro — Combina VWAP com Estrutura HH/LL</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        O setup de mais alta probabilidade é quando o pullback ao VWAP coincide com um <strong>Higher Low da estrutura intraday</strong>. Tens dois motivos para o preço reagir no mesmo nível — o VWAP atrai compradores institucionais e a estrutura HH/HL confirma que a tendência está intacta. Estes são os trades com menor risco e maior follow-through de toda a estratégia VWAP.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Imagens exemplificativas — EMA 200 Bounce */}
            {isEma200 ? (
              <div className="mb-8">
                <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-2">Exemplos Reais do Setup</h2>
                <p className="text-slate-500 text-sm mb-5">Abaixo podes ver como o EMA 200 Bounce se apresenta num gráfico real — o preço toca a linha e ressalta com força.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-2xl overflow-hidden border border-slate-100">
                    <div className="w-full h-52">
                      <img
                        src="https://readdy.ai/api/search-image?query=candlestick%20chart%20price%20bouncing%20off%20EMA%20200%20exponential%20moving%20average%20line%20uptrend%20bullish%20bounce%20pullback%20retest%20long%20entry%20dark%20trading%20chart%20clean%20professional&width=600&height=400&seq=ema200bounce1&orientation=landscape"
                        alt="EMA 200 Bounce — entrada long"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="bg-green-50 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-arrow-up-circle-fill text-green-600"></i>
                        </span>
                        <span className="text-green-700 font-semibold text-xs">Bounce Long — Preço acima da EMA 200</span>
                      </div>
                      <p className="text-slate-500 text-xs mt-1 leading-relaxed">Tendência de alta. O preço recua à EMA 200, forma vela de confirmação bullish e retoma a subida.</p>
                    </div>
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-slate-100">
                    <div className="w-full h-52">
                      <img
                        src="https://readdy.ai/api/search-image?query=candlestick%20chart%20price%20bouncing%20off%20EMA%20200%20line%20downtrend%20bearish%20rejection%20resistance%20moving%20average%20short%20entry%20trading%20chart%20dark%20professional%20clean&width=600&height=400&seq=ema200bounce2&orientation=landscape"
                        alt="EMA 200 Bounce — entrada short"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="bg-red-50 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-arrow-down-circle-fill text-red-500"></i>
                        </span>
                        <span className="text-red-600 font-semibold text-xs">Bounce Short — Preço abaixo da EMA 200</span>
                      </div>
                      <p className="text-slate-500 text-xs mt-1 leading-relaxed">Tendência de baixa. O preço sobe para testar a EMA 200, é rejeitado com vela bearish e continua a queda.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : !is1HCandle && !isHHLL && !isImpulse && !isVwap ? (
              <div className="w-full h-56 md:h-72 rounded-2xl overflow-hidden mb-8">
                <img
                  src={strategy.image.replace('seq=s', 'seq=sd')}
                  alt={`${strategy.name} em detalhe`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            ) : null}

            {/* Pros e Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <div className="bg-green-50 rounded-2xl p-5">
                <h3 className="font-bold text-green-700 text-base mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-thumb-up-fill text-green-600 text-lg"></i>
                  </span>
                  Pontos Fortes
                </h3>
                <ul className="space-y-3">
                  {strategy.pros.map((pro, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <span className="mt-0.5 w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-green-500 text-base"></i>
                      </span>
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-50 rounded-2xl p-5">
                <h3 className="font-bold text-red-600 text-base mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-thumb-down-fill text-red-500 text-lg"></i>
                  </span>
                  Pontos Fracos
                </h3>
                <ul className="space-y-3">
                  {strategy.cons.map((con, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <span className="mt-0.5 w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-close-line text-red-400 text-base"></i>
                      </span>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Indicadores */}
            <div className="mb-10">
              <h2 className="text-xl font-extrabold text-[#0d1f3c] mb-4">Indicadores Principais</h2>
              <div className="flex flex-wrap gap-2.5">
                {strategy.indicators.map((ind) => (
                  <span key={ind} className="bg-blue-50 border border-blue-100 text-blue-700 font-semibold text-sm px-4 py-2 rounded-full">
                    {ind}
                  </span>
                ))}
              </div>
            </div>

            {/* Compatibilidade Prop Firms */}
            <div className="bg-[#0d1f3c] rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-building-2-line text-blue-400 text-xl"></i>
                </span>
                <h3 className="font-bold text-white text-base">Compatibilidade com Prop Firms</h3>
              </div>
              <p className="text-blue-200 text-sm leading-relaxed">{strategy.propFirmCompatibility}</p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-5">

              {/* Resumo rápido */}
              <div className="bg-white border border-blue-100 rounded-2xl p-5">
                <h4 className="font-bold text-[#0d1f3c] text-sm mb-4">Resumo da Estratégia</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">Risco</span>
                    <span className={`font-semibold text-xs px-2.5 py-1 rounded-full ${riskColor}`}>{strategy.risk}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">Dificuldade</span>
                    <span className={`font-semibold text-xs px-2.5 py-1 rounded-full ${difficultyColor}`}>{strategy.difficulty}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">Timeframe</span>
                    <span className="font-semibold text-[#0d1f3c] text-xs">{strategy.timeframe}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">Win Rate</span>
                    <span className="font-semibold text-blue-600 text-xs">{strategy.winRate}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-400">Risco/Retorno</span>
                    <span className="font-semibold text-blue-600 text-xs">{strategy.riskReward}</span>
                  </div>
                </div>
              </div>

              {/* Ideal para */}
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-star-line text-blue-600"></i>
                  </span>
                  <h4 className="font-bold text-blue-800 text-sm">Ideal para</h4>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">{strategy.bestFor}</p>
              </div>

              {/* CTA */}
              <Link
                to="/estrategias"
                className="flex items-center justify-center gap-2 w-full border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Ver Todas as Estratégias
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Estratégias relacionadas */}
      {related.length > 0 && (
        <div className="bg-[#f0f6ff] py-14">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <h3 className="text-xl font-extrabold text-[#0d1f3c] mb-8">Outras Estratégias</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/estrategias/${rel.id}`}
                  className="group relative rounded-2xl overflow-hidden cursor-pointer"
                >
                  <div className="h-40 w-full">
                    <img src={rel.image} alt={rel.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c] via-[#0d1f3c]/30 to-transparent rounded-2xl"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex gap-2 mb-1.5">
                      <span className="bg-blue-600 text-white text-xs font-bold px-2.5 py-0.5 rounded-full">{rel.timeframe}</span>
                    </div>
                    <h4 className="text-white font-bold text-base">{rel.name}</h4>
                    <p className="text-slate-300 text-xs">{rel.tagline}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
